import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Ticket, 
  Calendar, 
  MapPin, 
  Mail, 
  Search, 
  Loader2,
  QrCode,
  CheckCircle,
  Clock,
  ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function GuestTickets() {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') || '';
  
  const [email, setEmail] = useState(initialEmail);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (initialEmail) {
      handleLookup(initialEmail);
    }
  }, [initialEmail]);

  const handleLookup = async (lookupEmail = email) => {
    if (!lookupEmail || !lookupEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setSearched(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/tickets/guest/lookup`, {
        params: { email: lookupEmail.toLowerCase() }
      });
      setTickets(response.data.tickets || []);
      
      if (response.data.count === 0) {
        toast.info('No tickets found for this email');
      }
    } catch (error) {
      console.error('Lookup error:', error);
      toast.error('Failed to look up tickets. Please try again.');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLookup();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    try {
      return format(new Date(dateStr), 'EEE, MMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="guest-tickets-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Ticket className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Find Your Tickets</h1>
          <p className="text-muted-foreground">
            Enter the email you used to purchase tickets to view them
          </p>
        </div>

        {/* Search Form */}
        <Card className="mb-8 bg-card/50 border-border/50">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="email" className="sr-only">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="pl-10"
                    data-testid="guest-email-lookup"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="gap-2" data-testid="lookup-tickets-btn">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Find Tickets
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && (
          <div className="space-y-4">
            {tickets.length > 0 ? (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Your Tickets ({tickets.length})</h2>
                </div>
                
                <div className="grid gap-4">
                  {tickets.map((ticket) => (
                    <Card key={ticket.ticket_id} className="overflow-hidden bg-card/50 border-border/50">
                      <div className="flex flex-col md:flex-row">
                        {/* Ticket Info */}
                        <div className="flex-1 p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="font-semibold text-lg mb-1">{ticket.event_title}</h3>
                              <Badge 
                                variant={ticket.status === 'confirmed' ? 'default' : 'secondary'}
                                className="gap-1"
                              >
                                {ticket.status === 'confirmed' ? (
                                  <CheckCircle className="w-3 h-3" />
                                ) : (
                                  <Clock className="w-3 h-3" />
                                )}
                                {ticket.status}
                              </Badge>
                            </div>
                            <Badge variant="outline" className="text-primary border-primary">
                              {ticket.ticket_type_name}
                            </Badge>
                          </div>
                          
                          <div className="space-y-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(ticket.event_date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{ticket.event_venue || 'Venue TBA'}</span>
                            </div>
                            {ticket.price > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-green-500">
                                  {({'NGN':'₦','USD':'$','CAD':'CA$','GBP':'£','EUR':'€','GHS':'GH₵','KES':'KSh','ZAR':'R'}[ticket.currency] || '₦')}{ticket.price.toLocaleString()} paid
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="mt-4 pt-4 border-t border-border">
                            <Link to={`/events/${ticket.event_id}`}>
                              <Button variant="outline" size="sm" className="gap-2">
                                View Event
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </Link>
                          </div>
                        </div>

                        {/* QR Code */}
                        <div className="flex flex-col items-center justify-center p-6 bg-muted/30 border-l border-border min-w-[200px]">
                          <div className="bg-white p-3 rounded-xl mb-3">
                            <img 
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${ticket.qr_code_data}`}
                              alt="Ticket QR Code"
                              className="w-[120px] h-[120px]"
                            />
                          </div>
                          <p className="text-xs font-mono text-primary">{ticket.qr_code_data}</p>
                          <p className="text-xs text-muted-foreground mt-1">Show at entrance</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card className="bg-card/50 border-border/50">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Ticket className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No tickets found</h3>
                  <p className="text-muted-foreground text-center mb-6 max-w-md">
                    We couldn't find any tickets associated with this email address. 
                    Make sure you're using the same email you used during checkout.
                  </p>
                  <Link to="/events">
                    <Button variant="outline" className="gap-2">
                      Browse Events
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Initial State - Before Search */}
        {!searched && (
          <Card className="bg-card/50 border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <QrCode className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Enter your email to find tickets</h3>
              <p className="text-muted-foreground text-center max-w-md">
                Search using the email address you provided when purchasing tickets to view and access your QR codes.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <div className="mt-8 p-6 rounded-xl bg-muted/30 border border-border/50">
          <h3 className="font-semibold mb-2">Need help?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            If you can't find your tickets, check your email inbox (including spam folder) for your ticket confirmation email.
            Each ticket has a unique QR code that you'll need to present at the event entrance.
          </p>
          <div className="flex gap-4">
            <Link to="/events">
              <Button variant="outline" size="sm">Browse Events</Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="sm">Sign In</Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
